import { EditGuesser, ListGuesser, Resource, ShowGuesser } from "react-admin";
import { Route } from "react-router-dom";
import { getResourceIcon } from "@/utils/icons";

// Icons - adjust imports based on your icon library
import {
  AdminPanelSettings as PermissionsIcon,
  Article as ClaimItemsIcon,
  Assignment as StoplossClaimsIcon,
  Business as PartnersIcon,
  Description as DocumentsIcon,
  Domain as ClientsIcon,
  Gavel as AuditIcon,
  LocalHospital as VendorsIcon,
  MonetizationOn as ReimbursementsIcon,
  People as UsersIcon,
  Person as ClaimantsIcon,
  Policy as PoliciesIcon,
  Security as RolesIcon,
  Task as TasksIcon,
  Transform as DocumentTransformationsIcon,
  Tune as TaskTemplatesIcon,
  HelpOutline as HelpArticlesIcon,
} from "@mui/icons-material";
import { ClientList } from "./clients/ClientList.tsx";
import { ClientShow } from "./clients/ClientShow.tsx";
import { ClaimantList } from "./claimants/ClaimantList.tsx";
import { ReimbursementList } from "@/resources/reimbursements/ReimbursementList";
import { PolicyList } from "./policies/PolicyList.tsx";
import { PolicyShow } from "./policies/PolicyShow.tsx";
import { ClaimantShow } from "./claimants/ClaimantShow.tsx";
import { DuplicateClaimantPairList } from "./duplicate-claimant-pairs/DuplicateClaimantPairList.tsx";
import { PolicyCreate } from "./policies/PolicyCreate.tsx";
import { UsersList } from "./admin/users/UsersList.tsx";
import { UsersShow } from "./admin/users/UsersShow.tsx";
import { UsersCreate } from "./admin/users/UsersCreate.tsx";
import { StoplossClaimsList } from "./stoploss-claims/StoplossClaimsList.tsx";
import { StoplossClaimsShow } from "./stoploss-claims/StoplossClaimsShow.tsx";
import { ReimbursementShow } from "./reimbursements/ReimbursementShow.tsx";
import { DocumentList } from "./documents/DocumentList.tsx";
import { DocumentShow } from "./documents/DocumentShow.tsx";
import { TaskShow } from "@/resources/tasks/TaskShow.tsx";
import { RolesList } from "./admin/roles/RolesList.tsx";
import { RolesShow } from "./admin/roles/RolesShow.tsx";
import { VendorsList } from "./vendors/VendorsList.tsx";
import { VendorsShow } from "./vendors/VendorsShow.tsx";
import { StoplossClaimsCreate } from "./stoploss-claims/StoplossClaimsCreate.tsx";
import { ClaimItemShow } from "./stoploss-claims/claimItems/ClaimItemShow.tsx";
import { ClaimItemsList } from "./stoploss-claims/claimItems/ClaimItemsList.tsx";
import { DocumentTransformationList } from "./document-transformations/DocumentTransformationList.tsx";
import { ReimbursementsCreate } from "./reimbursements/ReimbursementsCreate.tsx";
import { ResourceName } from "@/types";
import { PartnerList } from "./partners/PartnerList.tsx";
import { PartnerShow } from "./partners/PartnerShow.tsx";
import { TaskTemplateList } from "./admin/task-templates/TaskTemplateList.tsx";
import { HelpArticleList } from "./help/HelpArticleList.tsx";
import { HelpArticleCreate, HelpArticleEdit } from "./help/HelpArticleForm.tsx";
import { HelpArticleShow } from "./help/HelpArticleShow.tsx";

// RBAC Resources
const UserResource = (
  <Resource
    name={ResourceName.USERS}
    recordRepresentation={(r) => `${r?.full_name || r?.email}`}
    list={UsersList}
    show={UsersShow}
    create={UsersCreate}
    icon={UsersIcon}
  >
    <Route path=":userId/roles" element={<ListGuesser />} />
    <Route path=":userId/permissions" element={<ListGuesser />} />
    <Route path=":userId/tasks" element={<ListGuesser />} />
    <Route path=":userId/tasks/create" element={<EditGuesser />} />
  </Resource>
);

const RolesResource = (
  <Resource
    name={ResourceName.ROLES}
    recordRepresentation={(r) => `${r?.name}`}
    list={RolesList}
    show={RolesShow}
    create={EditGuesser}
    icon={RolesIcon}
  />
);

const PermissionResource = (
  <Resource
    name={ResourceName.PERMISSIONS}
    recordRepresentation={(r) => `${r?.name}`}
    list={ListGuesser}
    show={ShowGuesser}
    create={EditGuesser}
    icon={PermissionsIcon}
  />
);

// Partner & Client Resources
// TODO: deprecate the PartnerResource
const PartnerResource = (
  <Resource
    name={ResourceName.PARTNERS}
    recordRepresentation={(r) => `${r?.partner_company_name}`}
    list={PartnerList}
    show={PartnerShow}
    icon={PartnersIcon}
  >
    <Route path=":partnerId/users" element={<ListGuesser />} />
    <Route path=":partnerId/clients" element={<ListGuesser />} />
  </Resource>
);

const ClientResource = (
  <Resource
    name={ResourceName.CLIENTS}
    recordRepresentation={(r) => `${r?.client_legal_name}`}
    list={ClientList}
    show={ClientShow}
    create={EditGuesser}
    icon={ClientsIcon}
  >
    <Route path=":clientId/policies" element={<ListGuesser />} />
    <Route path=":clientId/policies/create" element={<EditGuesser />} />
    <Route path=":clientId/claimants" element={<ListGuesser />} />
    <Route path=":clientId/claimants/create" element={<EditGuesser />} />
    <Route path=":clientId/stoploss-claims" element={<ListGuesser />} />
    <Route path=":clientId/stoploss-claims/create" element={<EditGuesser />} />
    <Route path=":clientId/reimbursements" element={<ListGuesser />} />
    <Route path=":clientId/reimbursements/create" element={<EditGuesser />} />
    <Route path=":clientId/documents" element={<DocumentList />} />
    <Route path=":clientId/tasks" element={<ListGuesser />} />
    {/*TODO: implement this*/}
    {/*TODO: implement this*/}
    {/*<Route*/}
    {/*    path=":clientId/documents/upload"*/}
    {/*    element={<DocumentUpload />}*/}
    {/*/>*/}
  </Resource>
);

const VendorResource = (
  <Resource
    name={ResourceName.VENDORS}
    recordRepresentation={(r) => `${r?.company_name}`}
    list={VendorsList}
    show={VendorsShow}
    create={EditGuesser}
    icon={VendorsIcon}
  >
    <Route path="carriers" element={<VendorsList customResource="vendors/carriers" />} />
    <Route path="tpas" element={<VendorsList customResource="vendors/tpas" />} />
    <Route path="rx" element={<VendorsList customResource="vendors/rx" />} />
  </Resource>
);

// Policy & Related Resources
const PolicyResource = (
  <Resource
    name={ResourceName.POLICIES}
    recordRepresentation={(r) => `${r?.source_policy_id || r?.policy_aegle_id}`}
    list={PolicyList}
    show={PolicyShow}
    create={PolicyCreate}
    icon={PoliciesIcon}
  >
    <Route path=":policyId/endorsements" element={<ListGuesser />} />
    <Route path=":policyId/endorsements/create" element={<EditGuesser />} />
    <Route path=":policyId/endorsements/:endorsementId" element={<ShowGuesser />} />
    <Route path=":policyId/endorsements/:endorsementId/edit" element={<EditGuesser />} />
    <Route path=":policyId/lasered-claimants" element={<ListGuesser />} />
    <Route path=":policyId/lasered-claimants/create" element={<EditGuesser />} />
    <Route path=":policyId/lasered-claimants/:claimantId" element={<ShowGuesser />} />
    <Route path=":policyId/lasered-claimants/:claimantId/edit" element={<EditGuesser />} />
    <Route path=":policyId/specific-deductibles" element={<ListGuesser />} />
    <Route path=":policyId/specific-deductibles/create" element={<EditGuesser />} />
    <Route path=":policyId/specific-deductibles/:deductibleId" element={<ShowGuesser />} />
    <Route path=":policyId/specific-deductibles/:deductibleId/edit" element={<EditGuesser />} />
    <Route path=":policyId/aggregate-deductibles" element={<ListGuesser />} />
    <Route path=":policyId/aggregate-deductibles/create" element={<EditGuesser />} />
    <Route path=":policyId/aggregate-deductibles/:deductibleId" element={<ShowGuesser />} />
    <Route path=":policyId/aggregate-deductibles/:deductibleId/edit" element={<EditGuesser />} />
    <Route path=":policyId/stoploss-claims" element={<StoplossClaimsList />} />
    <Route path=":policyId/reimbursements" element={<ReimbursementList />} />
    <Route path=":policyId/documents" element={<DocumentList />} />
    {/*TODO: implement.  Note that the schema doesn't support this yet.*/}
    {/*<Route*/}
    {/*    path=":policyId/documents/upload"*/}
    {/*    element={<DocumentUpload />}*/}
    {/*/>*/}
  </Resource>
);

// Claims & Related Resources
const StoplossClaimResource = (
  <Resource
    name={ResourceName.STOPLOSS_CLAIMS}
    recordRepresentation={(r) => `Claim ${r?.id || "New"}`}
    list={StoplossClaimsList}
    show={StoplossClaimsShow}
    create={StoplossClaimsCreate}
    icon={StoplossClaimsIcon}
  >
    <Route
      path=":stoplossClaimId/items"
      element={<ListGuesser />}
      // Note that this will be the list of claim-items, not the list of stop-loss-claims.  See API doc.
    />
    <Route path=":stoplossClaimId/items/create" element={<EditGuesser />} />
    <Route path=":stoplossClaimId/items/:claimItemId" element={<ShowGuesser />} />
    <Route path=":stoplossClaimId/items/:claimItemId/edit" element={<EditGuesser />} />
    <Route path=":stoplossClaimId/reimbursements" element={<ListGuesser />} />
    <Route path=":stoplossClaimId/documents" element={<DocumentList />} />
    {/*TODO: implement.  Note that the schema doesn't support this yet.*/}
    {/*<Route*/}
    {/*    path=":stoplossClaimId/documents/upload"*/}
    {/*    element={<DocumentUpload />}*/}
    {/*/>*/}
  </Resource>
);

// Claim Items
const ClaimItemResource = (
  <Resource
    name={ResourceName.CLAIM_ITEMS}
    recordRepresentation={(r) => `Claim ${r?.id || "New"}`}
    show={ClaimItemShow}
    list={ClaimItemsList}
    icon={ClaimItemsIcon}
  />
);

const ClaimantResource = (
  <Resource
    name={ResourceName.CLAIMANTS}
    recordRepresentation={(r) => `${r?.claimant_first_name} ${r?.claimant_last_name}`}
    list={ClaimantList}
    show={ClaimantShow}
    create={EditGuesser}
    icon={ClaimantsIcon}
  >
    <Route path=":claimantId/stoploss-claims" element={<ListGuesser />} />
    <Route path=":claimantId/reimbursements" element={<ReimbursementList />} />
    <Route path=":claimantId/documents" element={<DocumentList />} />
    {/*TODO: implement.  Note that the schema doesn't support this yet.*/}
    {/*<Route*/}
    {/*    path=":claimantId/documents/upload"*/}
    {/*    element={<DocumentUpload />}*/}
    {/*/>*/}
  </Resource>
);

const ReimbursementResource = (
  <Resource
    name={ResourceName.REIMBURSEMENTS}
    recordRepresentation={(r) => `Reimbursement ${r?.id}`}
    list={ReimbursementList}
    show={ReimbursementShow}
    create={ReimbursementsCreate}
    icon={ReimbursementsIcon}
  />
);

// Document Management Resource
const DocumentResource = (
  <Resource
    name={ResourceName.DOCUMENTS}
    recordRepresentation={(r) => `${r?.name || r?.aegle_id || `Document #${r?.id}`}`}
    list={DocumentList}
    show={DocumentShow}
    create={EditGuesser}
    icon={DocumentsIcon}
  >
    {/*<Route*/}
    {/*    path="upload"*/}
    {/*    element={<DocumentUpload />}*/}
    {/*/>*/}
  </Resource>
);

const TaskTemplateResource = (
  <Resource
    name={ResourceName.TASK_TEMPLATES}
    recordRepresentation={(r) => `${r?.title}`}
    list={TaskTemplateList}
    icon={TaskTemplatesIcon}
  />
);

// Task & Audit Resources
// TODO: implement the guessers
const TaskResource = (
  <Resource
    name={ResourceName.TASKS}
    recordRepresentation={(r) => `${r?.title}`}
    list={ListGuesser}
    show={TaskShow}
    create={EditGuesser}
    icon={TasksIcon}
  >
    <Route path=":id/comments" element={<ListGuesser />} />
    <Route path=":id/comments/create" element={<EditGuesser />} />
    <Route path=":id/comments/:commentId/reply" element={<EditGuesser />} />
  </Resource>
);

const AuditResource = (
  <Resource
    name={ResourceName.AUDIT}
    recordRepresentation={(r) => `${r?.action} on ${r?.object_type}`}
    list={ListGuesser}
    show={ShowGuesser}
    icon={AuditIcon}
  />
);

// Developer/Admin Resources - Document Transformations.
// Show page intentionally not registered — its raw resource render (UUIDs,
// collapsed JSON arrays, raw enum values) was unfriendly to common users
// and is replaced by the in-page `TransformationDetailsDialog` accessed
// from the Mapping tab on a Document show.
const DocumentTransformationResource = (
  <Resource
    name={ResourceName.DOCUMENT_TRANSFORMATIONS}
    recordRepresentation={(r) => `${r?.id} - ${r?.status}`}
    list={DocumentTransformationList}
    icon={DocumentTransformationsIcon}
    options={{ label: "Document Transformations" }}
  />
);

// Export function to get all resources
// In-App Help / Knowledge Base (Feature 4317). help/categories is registered
// name-only to back the article category ReferenceField/ReferenceInput; it has
// no list so it does not appear in the menu (category management UI is N7).
const HelpCategoryResource = (
  <Resource name={ResourceName.HELP_CATEGORIES} recordRepresentation={(r) => `${r?.name}`} />
);

// Suspected duplicate claimants (#6717). Read-only in this slice: no show, no
// create. Triage actions (dismiss / bulk dismiss) and the merge action (#6519)
// land as follow-ups.
const DuplicateClaimantPairResource = (
  <Resource
    name={ResourceName.DUPLICATE_CLAIMANT_PAIRS}
    list={DuplicateClaimantPairList}
    icon={getResourceIcon(ResourceName.DUPLICATE_CLAIMANT_PAIRS)}
    options={{ label: "Duplicate Claimants" }}
  />
);

const HelpArticleResource = (
  <Resource
    name={ResourceName.HELP_ARTICLES}
    options={{ label: "Help / KB" }}
    recordRepresentation={(r) => `${r?.title}`}
    list={HelpArticleList}
    create={HelpArticleCreate}
    edit={HelpArticleEdit}
    show={HelpArticleShow}
    icon={HelpArticlesIcon}
  />
);

export const getAegleResources = [
  // RBAC Resources
  UserResource,
  RolesResource,
  PermissionResource,

  // Partner & Client Management
  PartnerResource,
  ClientResource,
  VendorResource,

  // Policy Management
  PolicyResource,

  // Claims Management
  StoplossClaimResource,
  ClaimItemResource,
  ClaimantResource,
  ReimbursementResource,

  // Document Management
  DocumentResource,

  // Duplicate claimant review (#6717)
  DuplicateClaimantPairResource,

  // Task & Audit
  TaskResource,
  TaskTemplateResource,
  AuditResource,

  // Developer Tools
  DocumentTransformationResource,

  // In-App Help / Knowledge Base (Feature 4317)
  HelpArticleResource,
  HelpCategoryResource,
];

export default getAegleResources;
